import React, { useState } from "react";

import {
  AuditEvent,
  AuditRow,
  auditAccessSentence,
  auditActionLabel,
  auditActorLabel,
  auditDeniedLabel,
  auditFilterLines,
  auditTimeLabel,
  fieldChangeLine,
} from "../lib/audit-history";
import { Button } from "./ui/Button";

/** Rows of a multi-row entry shown before the disclosure opens. */
const ROWS_SHOWN = 3;

const FieldLines: React.FC<{ row: AuditRow }> = ({ row }) => (
  <ul className="m-0 list-none space-y-0.5 p-0">
    {row.changes.map((change) => (
      <li
        key={change.field}
        className="break-words font-mono text-xs text-od-text"
      >
        {fieldChangeLine(change)}
      </li>
    ))}
  </ul>
);

const EventRows: React.FC<{ event: AuditEvent; showRowKey: boolean }> = ({
  event,
  showRowKey,
}) => {
  const [expanded, setExpanded] = useState(false);
  const rows = event.rows ?? [];
  const shown = expanded ? rows : rows.slice(0, ROWS_SHOWN);
  const rest = rows.length - shown.length;

  if (rows.length === 0) return null;

  return (
    <div className="space-y-1.5">
      {shown.map((row, index) => (
        <div key={`${row.rowKey ?? "row"}-${index}`} className="space-y-0.5">
          {showRowKey && row.rowKey && (
            <p className="m-0 text-xs font-medium text-od-muted">
              Row {row.rowKey}
            </p>
          )}
          {row.changes.length > 0 ? (
            <FieldLines row={row} />
          ) : (
            <p className="m-0 text-xs text-od-muted">No field values stored.</p>
          )}
        </div>
      ))}
      {rest > 0 && (
        <Button
          variant="link"
          size="sm"
          className="self-start"
          aria-expanded={expanded}
          onClick={() => setExpanded(true)}
        >
          Show {rest} more {rest === 1 ? "row" : "rows"}
        </Button>
      )}
      {expanded && rows.length > ROWS_SHOWN && (
        <Button
          variant="link"
          size="sm"
          className="self-start"
          aria-expanded={expanded}
          onClick={() => setExpanded(false)}
        >
          Show fewer rows
        </Button>
      )}
    </div>
  );
};

type AuditTimelineProps = {
  events: AuditEvent[];
  /**
   * The row drawer already names the row it has open, so its History tab hides
   * the row key. The Activity list covers every row and shows it.
   */
  showRowKey?: boolean;
  /** The Activity list names the tab; a row's own history does not need it. */
  showTab?: boolean;
};

const EventCard: React.FC<{
  event: AuditEvent;
  showRowKey: boolean;
  showTab: boolean;
}> = ({ event, showRowKey, showTab }) => {
  const sentence = auditAccessSentence(event);
  const filterLines = sentence ? auditFilterLines(event) : [];
  const deniedRead = event.action === "DENIED_READ";
  const headline =
    sentence ??
    `${auditActorLabel(event)} ${auditActionLabel(event).toLowerCase()}${
      showTab ? ` in ${event.tab}` : ""
    }`;

  let detail: React.ReactNode = null;
  if (deniedRead) {
    detail = null;
  } else if (event.result === "DENIED") {
    detail = (
      <p className="m-0 text-xs text-od-warning">
        {auditDeniedLabel(event.deniedReason)}. Nothing changed.
      </p>
    );
  } else if (sentence && filterLines.length > 0) {
    detail = (
      <ul className="m-0 list-none space-y-0.5 p-0">
        {filterLines.map((line) => (
          <li key={line} className="break-words font-mono text-xs text-od-text">
            {line}
          </li>
        ))}
      </ul>
    );
  } else if (!sentence) {
    detail = <EventRows event={event} showRowKey={showRowKey} />;
  }

  return (
    <li
      data-testid="audit-event"
      className="space-y-1.5 rounded-xl border border-od-border bg-od-surface p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p
          className={`m-0 text-sm font-medium ${
            deniedRead ? "text-od-warning" : "text-od-text"
          }`}
        >
          {headline}
        </p>
        <time
          dateTime={event.createdAt}
          className="shrink-0 text-xs text-od-muted"
        >
          {auditTimeLabel(event.createdAt)}
        </time>
      </div>

      {detail}
    </li>
  );
};

/**
 * One timeline of audit events, newest first (MOSA-368 slice 1).
 *
 * Shared by the row drawer's History tab and the Activity page so both read
 * the same: who, when, and each changed field as `old → new`. A refused
 * write shows the reason and no cell values, because the API stores none.
 * An access change or a denied read uses one plain sentence instead.
 */
export const AuditTimeline: React.FC<AuditTimelineProps> = ({
  events,
  showRowKey = true,
  showTab = true,
}) => (
  <ol className="m-0 list-none space-y-2 p-0">
    {events.map((event) => (
      <EventCard
        key={event.id}
        event={event}
        showRowKey={showRowKey}
        showTab={showTab}
      />
    ))}
  </ol>
);

export default AuditTimeline;
